/**
 * React Hook for Error Reporting
 *
 * Provides easy access to error reporting functions in React components.
 * Use this hook to:
 * - Add custom breadcrumbs
 * - Manually capture errors
 * - Track form submissions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { addBreadcrumb, captureError, trackFormSubmit } = useErrorReporting();
 *
 *   const handleClick = async () => {
 *     addBreadcrumb('User clicked important button');
 *     try {
 *       await riskyOperation();
 *     } catch (error) {
 *       captureError(error, { component: 'MyComponent' });
 *     }
 *   };
 * }
 * ```
 */

'use client';

import { useCallback } from 'react';
import {
  addBreadcrumb as addBreadcrumbCore,
  trackFormSubmission,
  trackFormFieldChange,
} from './breadcrumbs';
import { captureClientError, captureComponentError } from './reporter';
import type { BreadcrumbCategory, BreadcrumbLevel } from '../types';

/**
 * Hook return type
 */
interface UseErrorReportingReturn {
  /**
   * Add a custom breadcrumb to the trail.
   * @param message - Human-readable description
   * @param data - Optional additional data
   * @param level - Importance level (default: 'info')
   */
  addBreadcrumb: (
    message: string,
    data?: Record<string, unknown>,
    level?: BreadcrumbLevel
  ) => void;

  /**
   * Add a breadcrumb with custom category.
   * @param category - Breadcrumb category
   * @param message - Human-readable description
   * @param data - Optional additional data
   * @param level - Importance level (default: 'info')
   */
  addBreadcrumbWithCategory: (
    category: BreadcrumbCategory,
    message: string,
    data?: Record<string, unknown>,
    level?: BreadcrumbLevel
  ) => void;

  /**
   * Capture and send an error.
   * @param error - The error to capture
   * @param metadata - Additional context
   */
  captureError: (
    error: unknown,
    metadata?: Record<string, unknown>
  ) => Promise<void>;

  /**
   * Track a form submission.
   * @param formName - Identifier for the form
   * @param data - Optional sanitized form data
   */
  trackFormSubmit: (
    formName: string,
    data?: Record<string, unknown>
  ) => void;

  /**
   * Track a form field change.
   * @param formName - Identifier for the form
   * @param fieldName - Name of the changed field
   */
  trackFieldChange: (
    formName: string,
    fieldName: string
  ) => void;
}

/**
 * React hook for error reporting functionality.
 *
 * Provides memoized functions for adding breadcrumbs and capturing errors.
 * Should be used in client components only.
 *
 * @example
 * ```tsx
 * 'use client';
 *
 * function LeadForm() {
 *   const { addBreadcrumb, captureError, trackFormSubmit } = useErrorReporting();
 *
 *   const handleSubmit = async (data: FormData) => {
 *     trackFormSubmit('lead-form', { leadName: data.name });
 *
 *     try {
 *       await createLead(data);
 *       addBreadcrumb('Lead created successfully');
 *     } catch (error) {
 *       captureError(error, { formData: data });
 *     }
 *   };
 * }
 * ```
 */
export function useErrorReporting(): UseErrorReportingReturn {
  const addBreadcrumb = useCallback(
    (
      message: string,
      data?: Record<string, unknown>,
      level: BreadcrumbLevel = 'info'
    ) => {
      addBreadcrumbCore('custom', message, data, level);
    },
    []
  );

  const addBreadcrumbWithCategory = useCallback(
    (
      category: BreadcrumbCategory,
      message: string,
      data?: Record<string, unknown>,
      level: BreadcrumbLevel = 'info'
    ) => {
      addBreadcrumbCore(category, message, data, level);
    },
    []
  );

  const captureError = useCallback(
    async (error: unknown, metadata?: Record<string, unknown>) => {
      await captureClientError(error, 'client_runtime', metadata);
    },
    []
  );

  const trackFormSubmit = useCallback(
    (formName: string, data?: Record<string, unknown>) => {
      trackFormSubmission(formName, data);
    },
    []
  );

  const trackFieldChange = useCallback(
    (formName: string, fieldName: string) => {
      trackFormFieldChange(formName, fieldName);
    },
    []
  );

  return {
    addBreadcrumb,
    addBreadcrumbWithCategory,
    captureError,
    trackFormSubmit,
    trackFieldChange,
  };
}

export default useErrorReporting;
